import { useMemo } from "react";
import { circleOfFifths } from "../../../theory/circle";
import { keyName, keyScale, type MusicalKey } from "../../../theory/key";
import { Panel } from "../controls";
import { useProject, useServices } from "../../context";

const sig = (n: number) => (n === 0 ? "no ♯/♭" : n > 0 ? `${n}♯` : `${-n}♭`);
const sigText = (n: number) => (n === 0 ? "no sharps or flats" : n > 0 ? `${n} sharp${n > 1 ? "s" : ""}` : `${-n} flat${n < -1 ? "s" : ""}`);

function sharedNotes(a: MusicalKey, b: MusicalKey): number {
  const pa = new Set(keyScale(a).pitchClasses);
  return keyScale(b).pitchClasses.filter((pc) => pa.has(pc)).length;
}

/** Interactive circle of fifths. Choosing a key sets the project's written key (undoable). */
export function CircleOfFifths() {
  const { controller } = useServices();
  const key = useProject((p) => p.song.key);
  const circle = useMemo(() => circleOfFifths(), []);
  const idx = circle.findIndex((e) => (key.mode === "major" ? e.major.tonic === key.tonic : e.minor.tonic === key.tonic));
  const current = circle[idx]!;
  const left = circle[(idx + 11) % 12]!, right = circle[(idx + 1) % 12]!;
  const ownMajor = current.major;
  const setKey = (k: MusicalKey) => controller.updateProject((p) => ({ ...p, song: { ...p.song, key: k } }));

  const place = (i: number, r: number) => {
    const a = (i * 30 - 90) * (Math.PI / 180);
    return { left: `${50 + r * Math.cos(a)}%`, top: `${50 + r * Math.sin(a)}%` };
  };

  return (
    <Panel title="Circle of fifths">
      <div className="circle" role="group" aria-label="Circle of fifths: outer ring major keys, inner ring relative minor keys">
        {circle.map((e, i) => {
          const isMajor = key.mode === "major" && e.major.tonic === key.tonic;
          const isMinor = key.mode === "minor" && e.minor.tonic === key.tonic;
          return (
            <span key={e.major.tonic}>
              <button type="button" className={`circle-key major ${isMajor ? "current" : ""}`} style={place(i, 42)} aria-pressed={isMajor}
                aria-label={`${keyName(e.major)}, ${sigText(e.signature)}`} onClick={() => setKey(e.major)}>
                {e.major.tonic}<small aria-hidden="true">{sig(e.signature)}</small>
              </button>
              <button type="button" className={`circle-key minor ${isMinor ? "current" : ""}`} style={place(i, 25)} aria-pressed={isMinor}
                aria-label={`${e.minorName} minor, relative of ${keyName(e.major)}`} onClick={() => setKey(e.minor)}>
                {e.minorName}m
              </button>
            </span>
          );
        })}
        <div className="circle-centre" aria-live="polite">
          <strong>{keyName(key)}</strong>
          <span>{sigText(current.signature)}</span>
        </div>
      </div>
      <p className="hint">
        Clockwise adds a sharp (a fifth up); anticlockwise adds a flat. {keyName(ownMajor)}'s neighbours {keyName(left.major)} and {keyName(right.major)} each
        share {sharedNotes(ownMajor, right.major)} of 7 notes with it, which is why moving to them sounds smooth. The inner ring shows each relative minor (same notes).
        Selecting a key sets the project key (undo with Ctrl+Z).
      </p>
    </Panel>
  );
}
